import {IssueRelativeTime} from '@github-ui/ago'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {IssueStateIcons} from '@github-ui/issue-pr-state/Icons'
import {LabelToken} from '@github-ui/label-token'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {CommentIcon, LinkExternalIcon} from '@primer/octicons-react'
import {AvatarStack} from '@primer/react'

import type {EnrichedIssue, IssueStateReason, IssueStateType} from './types/list-types'

function getIssueStateIcon(state: IssueStateType, stateReason: IssueStateReason) {
  if (state === 'OPEN') return <IssueStateIcons.OPEN />
  if (stateReason === 'NOT_PLANNED') return <IssueStateIcons.NOT_PLANNED />
  return <IssueStateIcons.COMPLETED />
}

interface IssueListItemProps {
  issue: EnrichedIssue
  onFilterSelect?: (type: 'repo' | 'author' | 'label' | 'assignee', value: string) => void
}

export function IssueListItem({issue, onFilterSelect}: IssueListItemProps) {
  const stateIcon = getIssueStateIcon(issue.state, issue.stateReason)

  const labelBadges = issue.labels.map(label => {
    const token = (
      <LabelToken
        text={label.name}
        fillColor={label.color ? `#${label.color}` : undefined}
        style={{marginRight: '4px', cursor: onFilterSelect ? 'pointer' : undefined}}
      />
    )

    if (!onFilterSelect) {
      return <span key={label.id}>{token}</span>
    }

    return (
      <button
        key={label.id}
        type="button"
        className="btn-link"
        onClick={() => onFilterSelect('label', label.name)}
        aria-label={`Filter by label ${label.name}`}
      >
        {token}
      </button>
    )
  })

  const subIssuesText =
    issue.subIssuesSummary && issue.subIssuesSummary.total > 0
      ? `${issue.subIssuesSummary.completed}/${issue.subIssuesSummary.total} sub-issues`
      : null

  return (
    <ListItem
      key={issue.id}
      title={
        <ListItemTitle
          href={issue.permalink}
          value={issue.titleHtml ? <UnsafeHTMLText as="span" html={issue.titleHtml} /> : <span>{issue.title}</span>}
          linkProps={{'data-testid': 'issue-listitem-title-link'}}
          trailingBadges={labelBadges}
        />
      }
      metadata={
        <>
          {issue.linkedPullRequestCount != null && issue.linkedPullRequestCount > 0 ? (
            <ListItemMetadata>
              <LinkExternalIcon size={16} aria-label={`${issue.linkedPullRequestCount} linked pull requests`} />{' '}
              {issue.linkedPullRequestCount}
            </ListItemMetadata>
          ) : null}
          {issue.commentCount > 0 ? (
            <ListItemMetadata>
              <CommentIcon size={16} aria-label={`${issue.commentCount} comments`} /> {issue.commentCount}
            </ListItemMetadata>
          ) : null}
          {issue.assignees.length > 0 && (
            <ListItemMetadata alignment="right">
              <AvatarStack alignRight>
                {issue.assignees.map(assignee =>
                  onFilterSelect ? (
                    <button
                      key={assignee.login}
                      type="button"
                      className="btn-link"
                      onClick={() => onFilterSelect('assignee', assignee.login)}
                      aria-label={`Filter by assignee ${assignee.login}`}
                    >
                      <GitHubAvatar src={assignee.avatarUrl} size={20} alt="" />
                    </button>
                  ) : (
                    <GitHubAvatar key={assignee.login} src={assignee.avatarUrl} size={20} alt={assignee.login} />
                  ),
                )}
              </AvatarStack>
            </ListItemMetadata>
          )}
        </>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>{stateIcon}</ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription>
          <span>
            {issue.repoNameWithOwner}#{issue.number}
          </span>
          {issue.author && <span> opened by {issue.author.login}</span>}
          <span>
            {' '}
            <IssueRelativeTime datetime={issue.createdAt} />
          </span>
          {subIssuesText && <span> · {subIssuesText}</span>}
          {issue.blockedByCount != null && issue.blockedByCount > 0 && <span> · {issue.blockedByCount} blocked</span>}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}
